import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { type UUID } from '~/types/common';

import {
  type OrganizationalUnitListItem,
  useQueryOrganizationalUnitsInfinite,
} from '~/data/organizationalUnit';

import { Icon } from '@vestigas/ui/atoms';
import { Chip } from '~/components/baseComponents/chips/Chip';
import { Tooltip } from '~/ui/atoms';

import { MultiItemsManager } from './MultiItemsManager';
import { type EntityFilter, type Item } from './types';
import { getEntityFilter } from './utils';

const selectOrganizationalUnitsWithSyncState = (
  data: OrganizationalUnitListItem[],
) =>
  data?.map(({ id, isSynced, name }) => ({
    id,
    isSynced,
    name,
  }));

const getIsItemLocked = (item: Item): boolean => Boolean(item.isSynced);

/**
 * Renders a synced org-unit link as a read-only (non-removable) chip, while
 * keeping it clickable so the user can still navigate to the org-unit — same as
 * a non-synced chip. Returns `undefined` for non-synced items so `ChipList`
 * falls back to its default chip.
 */
const renderSyncedChip =
  (tooltip: string, onSelectedItemClick?: (item: Item) => void) =>
  (item: Item): ReactElement | undefined => {
    if (!item.isSynced) {
      return undefined;
    }

    return (
      <Tooltip title={tooltip} shouldWrapChildren>
        <Chip
          className="gap-1 bg-gray-200 text-tiga-text-lighter"
          clickable={Boolean(onSelectedItemClick)}
          id={item.id}
          name={
            <span className="flex items-center gap-1">
              {item.name}
              <Icon metaphor="sync" size="sm" />
            </span>
          }
          onClick={() => onSelectedItemClick?.(item)}
        />
      </Tooltip>
    );
  };

type P = {
  readonly isCreatingNewEntity?: boolean;
  readonly filterByEntity?: EntityFilter;
  readonly isDisabled?: boolean;
  readonly label?: string;
  readonly onChange?: (selectedIds: UUID[]) => void;
  readonly onSaveSelection?: ({
    added,
    removed,
  }: {
    added: UUID[];
    removed: UUID[];
  }) => void;
  readonly onSelectedItemClick?: (id: UUID) => void;
  readonly openSelectTestId?: string;
  readonly parentOrganizationalUnitId?: UUID;
};

export const MultiItemsManagerOrganizationalUnits = ({
  isCreatingNewEntity = false,
  filterByEntity,
  isDisabled = false,
  label = 'Organisations-Gruppen',
  onChange,
  onSaveSelection,
  onSelectedItemClick,
  parentOrganizationalUnitId,
  openSelectTestId,
}: P) => {
  const { t } = useTranslation('masterData');
  const syncedTooltip = t(($) => $.organizationalUnitAssignment.syncedTooltip);

  const filterEntityIn = getEntityFilter(
    'filterContainsEntity',
    filterByEntity,
  );
  const filterEntityNotIn = getEntityFilter(
    'filterNotContainsEntity',
    filterByEntity,
  );

  const useGetterFunctionSelectedItems = (searchString: string) => {
    const query = useQueryOrganizationalUnitsInfinite({
      ...filterEntityIn,
      filterParentOu: parentOrganizationalUnitId ?? undefined,
      searchString,
    });

    if (!isCreatingNewEntity) {
      return query;
    }

    return {
      data: [],
      fetchNextPage() {},
      hasNextPage: false,
      isFetchingNextPage: false,
      isLoading: false,
      totalCount: 0,
    };
  };

  const useGetterFunctionSelectableItems = (searchString: string) =>
    useQueryOrganizationalUnitsInfinite({
      ...filterEntityNotIn,
      filterNotParentOu: parentOrganizationalUnitId ?? undefined,
      searchString,
    });

  const renderSelectedChip = renderSyncedChip(
    syncedTooltip,
    onSelectedItemClick,
  );

  return (
    <MultiItemsManager
      fieldName="name"
      getIsItemLocked={getIsItemLocked}
      getterFunctionSelectableItems={useGetterFunctionSelectableItems}
      getterFunctionSelectedItems={useGetterFunctionSelectedItems}
      isDisabled={isDisabled}
      label={label}
      lockedItemTooltip={syncedTooltip}
      openSelectTestId={openSelectTestId}
      renderSelectedChip={renderSelectedChip}
      selectFunctionSelectedItems={selectOrganizationalUnitsWithSyncState}
      onChange={onChange}
      onSaveSelection={onSaveSelection}
      onSelectedItemClick={onSelectedItemClick}
    />
  );
};
